import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Clock } from "lucide-react";
import { scrollToId } from "../lib/smoothScroll";
import { btnPrimary, btnSecondary } from "../lib/contact";
import { PinMark } from "./PinMark";

const EASE = [0.22, 1, 0.36, 1];
const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/dc8a482d-361d-4996-9eb5-a4cbac9ec941/images/00aec3733a379d4a676ae48fcaf67942fd8bf51fd803dfe29919c4a3b05ddba1.jpeg";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-[#FAFAF9] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24">
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#1AD1B9]/18 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#0891B2]/12 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#00B4B4]/20 pl-2 pr-4 py-1.5 shadow-sm mb-7"
            data-testid="hero-badge"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center">
              <PinMark id="heroBadgePin" className="h-6 w-6" />
            </span>
            <span className="text-xs sm:text-sm font-semibold tracking-wide text-[#3B3A4E]">
              Recovery Concierge Service
            </span>
          </motion.div>

          <h1
            className="font-heading font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2D2B3D]"
            data-testid="hero-headline"
          >
            {["Get There With Someone", "Who's Been"].map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {l}{i === 1 ? " " : ""}
                  {i === 1 && <span className="grad-text">There.</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 text-lg text-[#5C5975] leading-relaxed max-w-xl"
          >
            Recovery-informed concierge transportation for treatment providers, recovery organizations,
            individuals, and families throughout Western Pennsylvania, Northwestern West Virginia, and the
            Morgantown area.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.68, ease: EASE }}
            className="mt-4 text-base text-[#5C5975]/90 leading-relaxed max-w-xl"
          >
            Whether someone is entering treatment, attending an appointment, returning to their recovery
            residence, or simply needs dependable transportation, Recovery Rides provides more than a ride
            from Point A to Point B.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button onClick={() => scrollToId("contact")} className={btnPrimary} data-testid="hero-request-service">
              Request Service <ArrowRight size={18} />
            </button>
            <button onClick={() => scrollToId("contact")} className={btnSecondary} data-testid="hero-call-text">
              <Phone size={18} /> Call or Text Us
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-5 flex items-center gap-2 text-sm text-[#5C5975]"
            data-testid="hero-availability"
          >
            <Clock size={15} className="text-[#0891B2]" />
            Immediate and scheduled transportation available 24/7, depending on driver availability.
          </motion.p>
        </div>

        {/* image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.35, ease: EASE }}
          className="relative"
        >
          <div className="relative rounded-[2rem] rounded-br-[4rem] overflow-hidden shadow-2xl shadow-[#3B3A4E]/20 aspect-[3/2] bg-[#F5F4F1]">
            <motion.img
              src={HERO_IMG}
              alt="Illustration of Michael, the Recovery Rides host, warmly welcoming an adult passenger beside a clean vehicle"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </div>

          {/* floating trust chip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1, ease: EASE }}
            className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl shadow-xl shadow-[#3B3A4E]/15 px-5 py-4 flex items-center gap-3 max-w-[16rem]"
          >
            <div className="h-11 w-11 shrink-0 rounded-xl brand-gradient flex items-center justify-center">
              <PinMark id="heroChipPin" className="h-6 w-6" solid />
            </div>
            <p className="text-sm font-medium text-[#3B3A4E] leading-snug">
              You're worth the ride. Dignity on every trip.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
